import { AxiosError } from 'axios';
import adserver from 'services/adserver';
import store from 'store';
import { useQuery } from '@tanstack/react-query';
import { ColumnFiltersState } from '@tanstack/react-table';
import { productsKeys } from './products.keys';
import { UseProductsInput, ProductListResponse } from './products.types';

const getProductList: {
  ({
    sorting,
    columnFilters,
    pagination,
  }: UseProductsInput): Promise<ProductListResponse>;
} = async ({ sorting, columnFilters, pagination }: UseProductsInput) => {
  const header = {
    Authorization: `Token ${store.getState().user.token}`,
  };
  let ordering = '';
  for (let i = 0; i < sorting.length; i++) {
    const id = sorting[i].id;
    const desc = sorting[i].desc ? '-' : '';
    ordering += `${desc}${id}`;
    if (i !== sorting.length - 1) {
      ordering += ',';
    }
  }

  const offset = pagination.pageIndex * pagination.pageSize;
  const limit = pagination.pageSize;

  const filters = (columnFilters as ColumnFiltersState).reduce(
    (acc: { [key: string]: any }, filter) => {
      if (
        filter.value !== undefined &&
        filter.value !== null &&
        filter.value !== ''
      ) {
        acc[filter.id] = Array.isArray(filter.value)
          ? filter.value.join(',')
          : filter.value;
      }
      return acc;
    },
    {},
  );

  const res = await adserver({
    url: '/products',
    method: 'GET',
    headers: header,
    params: {
      offset,
      limit,
      ...(ordering && { ordering }),
      ...filters,
    },
  });
  return res.data;
};

const getNewProductsCount = async (query: string): Promise<number> => {
  const response = await adserver({
    url: `/products?${query}`,
    headers: {},
    params: { limit: 1, offset: 0 },
  });
  return response.data.count;
};

export const useGetProductList = (
  { sorting, columnFilters, pagination }: UseProductsInput,
  enabled: boolean,
): {
  productListData: ProductListResponse | undefined;
  productListStatus: string;
  isProductListLoading: boolean;
  isProductListFetching: boolean;
} => {
  const {
    data: productListData,
    status: productListStatus,
    isLoading: isProductListLoading,
    isFetching: isProductListFetching,
  } = useQuery<ProductListResponse, AxiosError>({
    queryKey: productsKeys.list({ sorting, columnFilters, pagination }),
    queryFn: () => getProductList({ sorting, columnFilters, pagination }),
    refetchOnWindowFocus: true,
    keepPreviousData: true,
    enabled,
  });

  return {
    productListData,
    productListStatus,
    isProductListLoading,
    isProductListFetching,
  };
};

export const useGetNewProductsCount = (
  query: string,
): { count: number; isLoading: boolean; isFetching: boolean } => {
  const { data, isLoading, isFetching } = useQuery<number>({
    queryKey: productsKeys.newCount(query),
    queryFn: () => getNewProductsCount(query),
  });

  return { count: data ?? 0, isLoading, isFetching };
};
